import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Radio, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SIGNALS } from "@/constants/testIds";
import api from "@/lib/api";

/**
 * Promote a chat answer / alert breach into a pending Signal for review.
 * The caller supplies the frozen evidence + rule snapshot; this dialog just
 * captures a name, severity and an optional finding note.
 */
export function PromoteSignalDialog({
  open, onOpenChange, workspaceId, source = "manual",
  sourceRefId = null, alertId = null, defaultName = "", defaultSummary = "",
  evidence = {}, ruleSnapshot = {}, onCreated,
}) {
  const navigate = useNavigate();
  const [name, setName] = useState(defaultName);
  const [summary, setSummary] = useState(defaultSummary);
  const [severity, setSeverity] = useState("medium");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(defaultName || "");
      setSummary(defaultSummary || "");
      setSeverity("medium");
    }
  }, [open, defaultName, defaultSummary]);

  const submit = async () => {
    if (!name.trim() || !workspaceId) return;
    setSaving(true);
    try {
      const { data } = await api.post("/signals", {
        workspace_id: workspaceId,
        source,
        source_ref_id: sourceRefId,
        alert_id: alertId,
        name: name.trim(),
        summary: summary.trim(),
        severity,
        evidence,
        rule_snapshot: ruleSnapshot,
      });
      toast.success("Promoted to a pending signal", {
        description: "Review it in the Signals queue.",
        action: { label: "Open queue", onClick: () => navigate("/signals/queue") },
      });
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error("Couldn't promote", { description: e.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={SIGNALS.promoteDialog} className="max-w-md font-mono">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Radio className="h-4 w-4 text-accent" /> Promote to signal
          </DialogTitle>
          <DialogDescription className="text-xs">
            Freeze this finding as a reviewable signal. Approving it writes an immutable registry record.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Name</Label>
            <Input data-testid={SIGNALS.promoteName} value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Facility 3 occupancy drop" className="bg-card/60" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Finding (optional)</Label>
            <Textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={2}
              placeholder="What did we observe?" className="resize-none bg-card/60 text-sm" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Severity</Label>
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger data-testid={SIGNALS.promoteSeverity} className="bg-card/60"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button data-testid={SIGNALS.promoteSubmit} size="sm" onClick={submit} disabled={saving || !name.trim()}
            className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Radio className="mr-2 h-4 w-4" />}
            Promote
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
